<template>
  <div class="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="staff-department-card">
    <div class="p-3">
      <div class="flex items-center justify-between mb-2">
        <div class="flex items-center gap-2">
          <i class="pi pi-building text-primary text-sm"></i>
          <h3 class="font-semibold text-gray-800 text-sm">Organizational Assignment</h3>
        </div>
        <Button
          v-if="!isEditing && canEdit"
          :label="teacher.department ? 'Change' : 'Assign'"
          icon="pi pi-pencil"
          severity="secondary"
          text
          size="small"
          @click="startEditing"
          data-testid="btn-edit-department"
        />
      </div>

      <!-- Display mode -->
      <div v-if="!isEditing">
        <div v-if="teacher.department" class="flex flex-col gap-1.5">
          <div class="flex items-center gap-2 flex-wrap">
            <span class="text-xs text-gray-500">Faculty:</span>
            <Tag severity="secondary" class="text-xs" data-testid="faculty-tag">
              {{ currentFacultyName || '—' }}
            </Tag>
          </div>
          <div class="flex items-center gap-2 flex-wrap">
            <span class="text-xs text-gray-500">Department:</span>
            <Tag severity="info" class="text-xs" data-testid="department-tag">
              {{ teacher.department.department_name }}
            </Tag>
          </div>
        </div>
        <p v-else class="text-xs text-gray-400 italic">No faculty or department assigned</p>
      </div>

      <!-- Edit mode -->
      <div v-else class="space-y-2">
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-1">Faculty</label>
          <Select
            v-model="selectedFacultyId"
            :options="facultyOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Select a faculty"
            class="w-full"
            showClear
            filter
            filterPlaceholder="Search faculties..."
            data-testid="select-faculty"
            @change="onFacultyChange"
          />
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-1">Department</label>
          <Select
            v-model="selectedDepartmentId"
            :options="filteredDepartmentOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Select a department"
            class="w-full"
            showClear
            filter
            filterPlaceholder="Search departments..."
            :disabled="!selectedFacultyId"
            data-testid="select-department"
          />
          <small v-if="!selectedFacultyId" class="text-gray-400 text-xs">Select a faculty first</small>
        </div>
        <div class="flex items-center gap-1.5 justify-end">
          <Button
            label="Cancel"
            severity="secondary"
            text
            size="small"
            @click="cancelEditing"
            :disabled="isSaving"
          />
          <Button
            label="Save"
            icon="pi pi-check"
            size="small"
            @click="saveDepartment"
            :loading="isSaving"
            data-testid="btn-save-department"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useQuery, useMutation } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';
import { ASSIGN_STAFF_DEPARTMENT } from '@/graphql';
import { GET_DEPARTMENTS } from '@/graphql/Queries/tertiary';
import { can, AppPermissions } from '@/utils/permissions';
import { useUserStore } from '@/store/userStore';
import { useFaculties } from '@/composables/queries/useFaculties';
import type { Staff } from '@/types/composition-functions';

const props = defineProps<{
  teacher: Staff;
}>();

const emit = defineEmits<{
  updated: [];
}>();

const toast = useToast();
const userStore = useUserStore();

const canEdit = computed(() =>
  can(userStore.currentUser, AppPermissions.Teachers.Actions.Update, AppPermissions.Teachers.Resource),
);

const isEditing = ref(false);
const isSaving = ref(false);
const selectedDepartmentId = ref<string | null>(props.teacher.department_id ?? null);
const selectedFacultyId = ref<string | null>(props.teacher.department?.faculty_id ?? null);

const currentFacultyName = computed(() => props.teacher.department?.faculty?.faculty_name ?? null);

const { faculties } = useFaculties();

const { result: deptResult } = useQuery(GET_DEPARTMENTS, { first: 200 }, { enabled: isEditing });

const facultyOptions = computed(() => faculties.value.map((f) => ({
  label: f.faculty_name,
  value: f.id,
})));

const filteredDepartmentOptions = computed(() => {
  const departments: { id: string; department_name: string; faculty_id?: string | number | null }[]
    = deptResult.value?.listSchoolDepartments?.data ?? [];
  if (!selectedFacultyId.value) return [];
  return departments
    .filter((d) => String(d.faculty_id) === String(selectedFacultyId.value))
    .map((d) => ({
      label: d.department_name,
      value: d.id,
    }));
});

const { mutate: assignDepartment } = useMutation(ASSIGN_STAFF_DEPARTMENT);

function onFacultyChange() {
  selectedDepartmentId.value = null;
}

function startEditing() {
  selectedDepartmentId.value = props.teacher.department_id ?? null;
  selectedFacultyId.value = props.teacher.department?.faculty_id ?? null;
  isEditing.value = true;
}

function cancelEditing() {
  isEditing.value = false;
  selectedDepartmentId.value = props.teacher.department_id ?? null;
  selectedFacultyId.value = props.teacher.department?.faculty_id ?? null;
}

async function saveDepartment() {
  try {
    isSaving.value = true;
    await assignDepartment({
      staff_id: props.teacher.id,
      department_id: selectedDepartmentId.value || null,
    });
    toast.add({
      severity: 'success',
      summary: 'Updated',
      detail: selectedDepartmentId.value
        ? 'Organizational assignment updated successfully.'
        : 'Organizational assignment removed.',
      life: 3000,
    });
    isEditing.value = false;
    emit('updated');
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to update assignment.';
    toast.add({ severity: 'error', summary: 'Error', detail: message, life: 5000 });
  } finally {
    isSaving.value = false;
  }
}
</script>
